import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  SimpleGrid,
  Card,
  Badge,
  Group,
  TextInput,
  Skeleton,
} from '@mantine/core';
import { IconSearch, IconBuildingChurch, IconArrowRight } from '@tabler/icons-react';
import {
  usePublicMinistryService,
  usePublicCoursesService,
} from '@actschurch/ccms-sdk';
import { ministryHref } from '@/utils/slug';

/**
 * Ministries browse page. Lists every ministry as a card showing the live
 * course count from the public catalog so visitors can pick where to start.
 */
export default function MinistriesIndexPage() {
  const publicMinistryService = usePublicMinistryService();
  const publicCoursesService = usePublicCoursesService();
  const [q, setQ] = useState('');

  const { data: ministries = [], isLoading: loadingMinistries } = useQuery({
    queryKey: ['public', 'ministries'],
    queryFn: () => publicMinistryService.list(),
  });
  const { data: courses = [] } = useQuery({
    queryKey: ['public', 'courses', 'all'],
    queryFn: () => publicCoursesService.list(),
  });

  const courseCountByMinistry = useMemo(() => {
    const m = new Map<number, number>();
    for (const c of courses) {
      if (c.ministryID != null) m.set(c.ministryID, (m.get(c.ministryID) ?? 0) + 1);
    }
    return m;
  }, [courses]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ministries;
    return ministries.filter(
      (m) =>
        (m.name ?? '').toLowerCase().includes(needle) ||
        (m.description ?? '').toLowerCase().includes(needle),
    );
  }, [ministries, q]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={1}>Ministries</Title>
        <Text c="dimmed">
          Pick a ministry to see every public course it offers.
        </Text>
      </Stack>

      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Search ministries"
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        size="md"
        radius="xl"
      />

      {loadingMinistries ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={170} radius="lg" />
          ))}
        </SimpleGrid>
      ) : filtered.length === 0 ? (
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="sm">
            <IconBuildingChurch size={36} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">No ministries match your search.</Text>
          </Stack>
        </Card>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {filtered.map((m) => {
            const count = courseCountByMinistry.get(m.ministryID) ?? 0;
            return (
              <Card
                key={m.ministryID}
                component={Link}
                to={ministryHref(m)}
                shadow="sm"
                padding="lg"
                radius="lg"
                withBorder
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <IconBuildingChurch size={20} color="var(--mantine-color-brand-6)" />
                    <Title order={4}>{m.name}</Title>
                  </Group>
                  <Badge variant="light" color="brand">
                    {count} course{count === 1 ? '' : 's'}
                  </Badge>
                </Group>
                {m.description && (
                  <Text size="sm" c="dimmed" lineClamp={3}>
                    {m.description}
                  </Text>
                )}
                <Group justify="flex-end" mt="md">
                  <Text size="xs" c="brand.6" fw={600}>
                    Browse courses <IconArrowRight size={12} style={{ verticalAlign: 'middle' }} />
                  </Text>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}
